/**
 * @fileoverview Map Module - Handles Google Map initialization, marker creation,
 *               clustering, info window display, and custom map controls.
 * @module MapModule
 */
const MapModule = (function() {
  /**
   * Simple logging utility specific to the MapModule.
   * @namespace Logger
   */
  const Logger = {
    /**
     * Gets the current timestamp in ISO format.
     * @returns {string} ISO timestamp string.
     */
    timestamp: function() {
      return new Date().toISOString();
    },
    /**
     * Logs a standard message.
     * @param {string} message - The message to log.
     */
    log: function(message) {
      console.log(`[${this.timestamp()}] [MapModule] ${message}`);
    },
    /**
     * Logs an error message.
     * @param {string} message - The error description.
     * @param {*} [error] - Optional error object or details.
     */
    error: function(message, error) {
      console.error(`[${this.timestamp()}] [MapModule] ERROR: ${message}`, error || '');
    },
    /**
     * Logs a warning message.
     * @param {string} message - The warning message.
     */
    warn: function(message) {
      console.warn(`[${this.timestamp()}] [MapModule] WARNING: ${message}`);
    },
    /**
     * Logs an informational message.
     * @param {string} message - The info message.
     */
    info: function(message) {
      // console.info(`[${this.timestamp()}] [MapModule] INFO: ${message}`);
    },
    /**
     * Logs a debug message.
     * @param {string} message - The debug message.
     */
    debug: function(message) {
      // console.debug(`[${this.timestamp()}] [MapModule] DEBUG: ${message}`); // Debug logs disabled by default
    }
  };


  /**
   * The Google Map instance.
   * @type {google.maps.Map | null}
   * @private
   */
  let map = null;
  /**
   * The single InfoWindow instance used for displaying marker details.
   * @type {google.maps.InfoWindow | null}
   * @private
   */
  let infoWindow = null;
  /**
   * The MarkerClusterer instance (@googlemaps/markerclusterer).
   * @type {markerClusterer.MarkerClusterer | null}
   * @private
   */
  let markerCluster = null; // MarkerClusterer instance
  /**
   * Stores all created google.maps.Marker objects, keyed by property ID.
   * @type {Object<string, google.maps.Marker>}
   * @private
   */
  let allMarkers = {};  // Object indexed by property ID
  /**
   * Stores all created google.maps.Marker objects in an array for iteration.
   * @type {Array<google.maps.Marker>}
   * @private
   */
  let markersArray = []; // Array version for iteration

  /**
   * Stores all markers that match the current filter criteria, regardless of viewport.
   * Used for updating visible markers when the map bounds change.
   * @type {Array<google.maps.Marker>}
   * @private
   */
  let allFilteredMarkers = [];

  /**
   * Stores the property IDs of markers that have been clicked during this session.
   * Used to track which markers should display in the "visited" state.
   * @type {Set<string>}
   * @private
   */
  let clickedMarkers = new Set();

  /**
   * Flag to prevent URL update loops when opening popup from URL state.
   * @type {boolean}
   * @private
   */
  let updatingPropertyFromUrl = false;

  //======== URL STATE HELPERS ========

  /**
   * Gets a marker by the property's URL ID (stable across data rebuilds).
   *
   * CONTEXT: Used for deep-linking to a specific property via URL.
   * UPSTREAM: Called by openPropertyPopupByUrlId().
   * DOWNSTREAM: Returns the marker if found, null otherwise.
   *
   * @param {string|number} urlid - The URL ID to search for.
   * @returns {google.maps.Marker|null} The marker if found, null otherwise.
   * @private
   */
  function getMarkerByUrlId(urlid) {
    const property = DataManager.getItemByUrlId(urlid);
    if (property && property.id && allMarkers[property.id]) {
      return allMarkers[property.id];
    }
    return null;
  }

  /**
   * Opens the InfoWindow for a property specified by URL ID.
   * Pans and zooms the map to show the property marker.
   *
   * CONTEXT: Called on page load if ?property= is in URL, or on back/forward.
   * UPSTREAM: Called from init and UrlState.onChange() handler.
   * DOWNSTREAM: Opens InfoWindow, pans map, updates marker state.
   * FAILURE: Silently returns if property not found (URL may contain stale ID).
   *
   * @param {string|number} urlid - The URL ID of the property to show.
   * @private
   */
  function openPropertyPopupByUrlId(urlid) {
    const marker = getMarkerByUrlId(urlid);
    if (!marker || !map || !infoWindow) {
      Logger.warn(`Property with urlid ${urlid} not found or map not ready`);
      return;
    }

    // Track as clicked
    if (!clickedMarkers.has(marker.propertyId)) {
      clickedMarkers.add(marker.propertyId);
      updateMarkerToClickedState(marker);
    }

    // Pan and zoom to the marker
    const position = marker.getPosition();
    map.panTo(position);
    map.setZoom(16);

    // Open the InfoWindow
    const content = createInfoWindowContent(marker);
    infoWindow.setContent(content);
    infoWindow.open(map, marker);
  }

  /**
   * Writes the selected property URL ID to the URL.
   * Called when a marker is clicked.
   *
   * @param {string|number} propertyId - The property ID of the clicked marker.
   * @private
   */
  function writePropertyToUrl(propertyId) {
    if (updatingPropertyFromUrl) return;

    const property = DataManager.getItemById(propertyId);
    if (property && property.urlid) {
      UrlState.set({ property: property.urlid });
    }
  }

  /**
   * Clears the property parameter from URL (e.g., when InfoWindow is closed).
   * @private
   */
  function clearPropertyFromUrl() {
    if (updatingPropertyFromUrl) return;
    UrlState.set({ property: null });
  }

  /**
   * Updates a marker's icon to the "clicked" state (white with blue border).
   * @param {google.maps.Marker} marker - The marker to update.
   * @private
   */
  function updateMarkerToClickedState(marker) {
    marker.setIcon({
      path: google.maps.SymbolPath.CIRCLE,
      fillColor: '#ffffff',
      fillOpacity: 1,
      strokeColor: '#1377ba',
      strokeWeight: 2,
      scale: 10
    });
  }

  /**
   * Creates the HTML content for the info window's loading state.
   * Displays a spinner and loading text.
   * @returns {string} HTML string for the loading indicator.
   * @private
   */
  function createLoadingContent() {
    return `
      <div class="info-window">
        <div class="d-flex justify-content-center my-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">${t('map.loadingDetails')}</span>
          </div>
        </div>
        <p class="text-center">${t('map.loadingDetails')}</p>
      </div>
    `;
  }

  /**
   * Generates the location source text based on the coordinates_source value.
   * @param {string} coordinatesSource - The source of the coordinates.
   * @returns {string} HTML string for the location source text or empty string if not applicable.
   * @private
   */
  function buildLocationSourceText(coordinatesSource) {
    if (!coordinatesSource) return '';

    // Map coordinates_source to i18n keys
    const sourceKey = 'locationSource.' + coordinatesSource.replace(/_/g, '_');
    const translation = t(sourceKey, { fallback: '' });

    if (translation) {
      return `<div class="location-source">${translation}</div>`;
    }
    return ''; // Hide text for any unknown value
  }

  /**
   * Creates the HTML content for the marker's info window.
   * Fetches full property details from DataManager using the marker's stored propertyId.
   * Shows loading content if data isn't ready yet.
   * @param {google.maps.Marker} marker - The marker object clicked by the user. Must have a `propertyId` property.
   * @returns {string} HTML string for the info window content.
   * @private
   */
  function createInfoWindowContent(marker) {
    // Get the property data from the DataManager using the property ID
    const propertyId = marker.propertyId;
    const fullDetails = DataManager.getItemById(propertyId);
    
    // If data not loaded yet, show loading animation
    if (!fullDetails || !fullDetails.status) {
      return createLoadingContent();
    }
    
    // Build price information
    const prices = buildPriceInfo(fullDetails);
    
    // Build bedroom information
    const bedrooms = buildBedroomInfo(fullDetails);
    
    // Extract domain name from URL for source attribution
    const sourceDomain = extractDomain(fullDetails.url_page);
    
    // Build the image HTML
    const imageHtml = buildImageHtml(fullDetails, sourceDomain);
    
    // Build realtor link
    const realtorLink = fullDetails.realtor_name && fullDetails.url_page
      ? `<div class="realtor-info">${t('property.realtor')} <a href="${addUtmParameters(fullDetails.url_page)}" target="_blank" onclick="AppUtils.trackListingClick('${fullDetails.realtor_id || ''}');">${fullDetails.realtor_name}</a></div>`
      : '';
      
    // Build location source text
    const locationSourceText = buildLocationSourceText(fullDetails.coordinates_source);

    // Create the full info window content - clean and compact
    const propertyLabel = t('propertyType.property');
    return `
      <div class="info-window" data-urlid="${fullDetails.urlid || ''}" data-title="${(fullDetails.property_title || propertyLabel).replace(/"/g, '&quot;')}">
        <h2>${fullDetails.property_title || propertyLabel}</h2>
        ${imageHtml}
        <div class="details-row">
          <div class="details">
            ${prices} ${bedrooms ? `&bull; ${bedrooms}` : ''}
          </div>
        </div>
        ${realtorLink}
        <div class="description">${fullDetails.description || ''}</div>
        ${fullDetails.url_page ? `
        <div class="info-window-buttons">
          <a href="#" class="share-btn btn btn-sm btn-primary" title="${t('action.share')}">${t('action.share')}</a>

          <a href="${addUtmParameters(fullDetails.url_page)}" target="_blank" class="view-details-btn btn btn-sm btn-primary" onclick="AppUtils.trackListingClick('${fullDetails.realtor_id || ''}');">${t('action.viewDetails')}</a>
          <div class="share-menu" style="display: none;">
            <button class="share-option" data-platform="facebook">
              <i class="bi bi-facebook"></i> ${t('share.facebook')}
            </button>
            <button class="share-option" data-platform="whatsapp">
              <i class="bi bi-whatsapp"></i> ${t('share.whatsapp')}
            </button>
            <button class="share-option" data-platform="email">
              <i class="bi bi-envelope"></i> ${t('share.email')}
            </button>
            <button class="share-option" data-platform="copy">
              <i class="bi bi-link-45deg"></i> ${t('share.copyLink')}
            </button>
          </div>
        </div>` : ''}
        ${locationSourceText}
      </div>
    `;
  }

  /**
   * Formats a number or string representation of a number using German locale
   * to get dot separators for thousands. Handles invalid/empty inputs.
   * @param {string|number|null|undefined} numStr - The number or string to format.
   * @returns {string} The formatted number string (e.g., "1.200.000") or the original
   *                   string if it cannot be parsed as a number, or an empty string for null/undefined/empty.
   * @private
   */
  function formatNumberWithDots(numStr) {
    if (numStr === null || numStr === undefined || numStr === 'NULL' || numStr === '') {
      return ''; // Return empty for invalid/null inputs
    }
    // Remove any existing formatting (like commas) and parse
    const num = parseFloat(String(numStr).replace(/[^0-9.-]+/g,""));
    if (isNaN(num)) {
      return String(numStr); // Return original if parsing fails
    }
    // Use German locale for dot separators
    return num.toLocaleString('de-DE', { maximumFractionDigits: 0 }); // Assuming whole numbers for prices
  }

  /**
   * Builds the HTML string for displaying the price in the info window.
   * Prioritizes USD, then NAF, then EUR. Uses `formatNumberWithDots`.
   * Returns "Price on request" if no valid price is found.
   * @param {object} listing - The property data object.
   * @returns {string} HTML string for the price display (e.g., "<strong>USD 1.200.000</strong>").
   * @private
   */
  function buildPriceInfo(listing) {
    let priceHtml = '';
    let formattedPrice = '';
  
    // Prioritize USD, then NAF, then EUR
    if (listing.price_usd && listing.price_usd !== 'NULL') {
      formattedPrice = formatNumberWithDots(listing.price_usd);
      if (formattedPrice) priceHtml = `<strong>USD ${formattedPrice}</strong>`;
    } else if (listing.price_naf && listing.price_naf !== 'NULL') {
      formattedPrice = formatNumberWithDots(listing.price_naf);
      if (formattedPrice) priceHtml = `<strong>XCG ${formattedPrice}</strong>`;
    } else if (listing.price_eur && listing.price_eur !== 'NULL') {
      formattedPrice = formatNumberWithDots(listing.price_eur);
      if (formattedPrice) priceHtml = `<strong>EUR ${formattedPrice}</strong>`;
    }
  
    return priceHtml || `<span>${t('price.onRequest')}</span>`; // Use span for inline display
  }

  /**
   * Builds the HTML string for displaying the number of bedrooms in the info window.
   * Returns an empty string if no valid bedroom count is found.
   * @param {object} listing - The property data object.
   * @returns {string} HTML string for the bedroom display (e.g., "<span>3 bedrooms</span>").
   * @private
   */
  function buildBedroomInfo(listing) {
    if (!listing.bedrooms || listing.bedrooms === 'NULL' || listing.bedrooms === '0') return ''; // Also check for 0 bedrooms

    const count = parseInt(listing.bedrooms, 10);
    return `<span>${count} ${t('property.bedroom', { count: count })}</span>`; // Use span for inline display
  }

  /**
   * Extracts the hostname (domain) from a given URL string.
   * Returns an empty string if the URL is invalid or missing.
   * @param {string} url - The URL string.
   * @returns {string} The extracted domain name (e.g., "example.com") or an empty string.
   * @private
   */
  function extractDomain(url) {
    if (!url) return '';

    try {
      return new URL(url).hostname;
    } catch (e) {
      return '';
    }
  }

  /**
   * Adds UTM parameters to a URL for tracking purposes.
   * Handles URLs that already have query parameters.
   * @param {string} url - The original URL.
   * @returns {string} URL with UTM parameters added.
   * @private
   */
  function addUtmParameters(url) {
    if (!url) return '';

    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('utm_source', 'caribbeanhousehunt.com');
      urlObj.searchParams.set('utm_medium', 'referral');
      urlObj.searchParams.set('utm_campaign', 'caribbeanhousehunt');
      return urlObj.toString();
    } catch (e) {
      // If URL parsing fails, append parameters manually
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}utm_source=caribbeanhousehunt.com&utm_medium=referral&utm_campaign=caribbeanhousehunt`;
    }
  }

  /**
   * Builds the HTML for the image thumbnail display in the info window.
   * Includes the image, lazy loading, placeholder fallback, and source attribution link.
   * @param {object} listing - The property data object.
   * @param {string} sourceDomain - The extracted domain name for the source link.
   * @returns {string} HTML string for the image container.
   * @private
   */
  function buildImageHtml(listing, sourceDomain) {
    const placeholderSvg = AppUtils.getPlaceholderImageSvg();

    if (listing.image_url) {
      const sourceAttribution = sourceDomain 
        ? `<div class="image-source">Source: <a href="${addUtmParameters(listing.url_page)}" target="_blank" onclick="AppUtils.trackListingClick('${listing.realtor_id || ''}');">${sourceDomain}</a></div>`
        : '';
        
      return `
        <div class="thumbnail-container">
          <img 
            src="/map-assets/property-images/${listing.image_url}" 
            alt="${listing.property_title || 'Property'}" 
            class="thumbnail" 
            loading="lazy"
            onerror="this.onerror=null; this.src='${placeholderSvg}'; this.classList.add('placeholder-img');" 
          >
          ${sourceAttribution}
        </div>`;
    } else {
      return `
        <div class="thumbnail-container no-image">
          <div class="placeholder-img">
            <span>${t('property.noImage')}</span>
          </div>
        </div>`;
    }
  }

  // ======== SHARE FUNCTIONALITY ========

  /**
   * Builds a shareable URL for the given property.
   * CONTEXT: Used by share menu to create deep-linkable URLs for social sharing.
   * UPSTREAM: Called by handleShareClick() when user selects a share option.
   * DOWNSTREAM: URL is opened in new window or copied to clipboard.
   * @param {string} urlid - The URL ID of the property.
   * @returns {string} The full shareable URL.
   * @private
   */
  function buildShareUrl(urlid) {
    return `${window.location.origin}${window.location.pathname}?property=${encodeURIComponent(urlid)}`;
  }

  /**
   * Shows a toast notification at the bottom of the screen.
   * CONTEXT: Provides user feedback for actions like copying link to clipboard.
   * UPSTREAM: Called by handleShareClick() after successful copy operation.
   * DOWNSTREAM: Toast auto-dismisses after 2.5 seconds.
   * @param {string} message - The message to display in the toast.
   * @private
   */
  function showToast(message) {
    // Remove any existing toast
    const existingToast = document.querySelector('.share-toast');
    if (existingToast) {
      existingToast.remove();
    }

    // Create new toast element
    const toast = document.createElement('div');
    toast.className = 'share-toast';
    toast.textContent = message;
    document.body.appendChild(toast);

    // Trigger fade in
    requestAnimationFrame(() => {
      toast.classList.add('show');
    });

    // Auto-dismiss after 2.5 seconds
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, 2500);
  }

  /**
   * Handles share menu option clicks.
   * CONTEXT: Part of the share functionality for property info windows.
   * UPSTREAM: Called by click event listener on share menu options.
   * DOWNSTREAM: Opens share URL in new window or copies to clipboard.
   * FAILURE: Copy operation may fail on older browsers - falls back to alert.
   * @param {string} platform - The share platform ('facebook', 'whatsapp', 'email', 'copy').
   * @param {string} urlid - The URL ID of the property.
   * @param {string} title - The property title for share text.
   * @private
   */
  function handleShareClick(platform, urlid, title) {
    const shareUrl = buildShareUrl(urlid);
    const shareText = t('share.subjectText') + title;

    switch (platform) {
      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
          '_blank',
          'width=600,height=400'
        );
        break;

      case 'whatsapp':
        window.open(
          `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + '\n' + shareUrl)}`,
          '_blank'
        );
        break;

      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(t('share.emailBody') + shareUrl)}`;
        break;

      case 'copy':
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(shareUrl)
            .then(() => showToast(t('toast.linkCopied')))
            .catch(() => {
              // Fallback for clipboard errors
              showToast(t('toast.copyError'));
            });
        } else {
          // Fallback for older browsers
          showToast(t('toast.copyNotSupported'));
        }
        break;

      default:
        Logger.warn('Unknown share platform:', platform);
    }
  }

  /**
   * Sets up global event listeners for the share functionality.
   * CONTEXT: Uses event delegation on document for reliable click handling.
   * UPSTREAM: Called once during map initialization.
   * DOWNSTREAM: Triggers handleShareClick when user selects a share option.
   * @private
   */
  function setupShareEventListeners() {
    // Use event delegation on document - more reliable than domready
    document.addEventListener('click', (e) => {
      // Handle share button click
      const shareBtn = e.target.closest('.share-btn');
      if (shareBtn) {
        e.preventDefault();
        e.stopPropagation();

        // Find the share menu - check info window containers and card share wrapper
        const container = shareBtn.closest('.info-window-actions') ||
                          shareBtn.closest('.info-window-buttons') ||
                          shareBtn.closest('.card-share-wrapper');
        if (!container) return;

        const shareMenu = container.querySelector('.share-menu');
        if (!shareMenu) return;

        // Close any other open share menus first
        document.querySelectorAll('.share-menu[style*="block"]').forEach(menu => {
          if (menu !== shareMenu) menu.style.display = 'none';
        });

        const isVisible = shareMenu.style.display !== 'none';
        shareMenu.style.display = isVisible ? 'none' : 'block';
        return;
      }

      // Handle share option click
      const shareOption = e.target.closest('.share-option');
      if (shareOption) {
        e.stopPropagation();
        // Check for info window or property card (list view)
        const parentEl = shareOption.closest('.info-window') || shareOption.closest('.property-card');
        if (!parentEl) return;

        const shareMenu = shareOption.closest('.share-menu');
        const urlid = parentEl.dataset.urlid;
        const title = parentEl.dataset.title;
        const platform = shareOption.dataset.platform;

        handleShareClick(platform, urlid, title);
        if (shareMenu) shareMenu.style.display = 'none';
        return;
      }

      // Close menu when clicking outside
      const openMenu = document.querySelector('.share-menu[style*="block"]');
      if (openMenu && !e.target.closest('.share-menu')) {
        openMenu.style.display = 'none';
      }
    });
  }

  /**
   * Filters markers to only include those within the current map bounds.
   * @param {Array<google.maps.Marker>} markers - Array of markers to filter.
   * @param {google.maps.LatLngBounds} bounds - The current map bounds.
   * @returns {Array<google.maps.Marker>} Array of markers within the bounds.
   * @private
   */
  function filterMarkersByBounds(markers, bounds) {
    if (!bounds) return markers;
    
    const visibleMarkers = [];
    
    for (const marker of markers) {
      if (bounds.contains(marker.getPosition())) {
        visibleMarkers.push(marker);
      }
    }
    
    
    return visibleMarkers;
  }

  /**
   * Shows only the markers whose property IDs are in the provided array.
   * Hides all other markers by removing them from the map.
   * Updates the MarkerClusterer with the set of visible markers.
   * Only shows markers that are within the current map bounds.
   * @param {Array<string|number>} idsToShow - An array of property IDs to display.
   * @returns {number} The count of markers that match the filter criteria (not just visible ones).
   * @private
   */
  function showMarkersByIds(idsToShow) {
    
    // Convert array of IDs to show into a Set of *strings* for consistent and fast lookups.
    const idsSet = new Set(idsToShow.map(String)); 
    
    // First, ensure all markers are removed from the map
    markersArray.forEach(marker => marker.setMap(null));
    
    // Collect all markers that match the filter criteria
    allFilteredMarkers = [];
    for (const id in allMarkers) {
      if (idsSet.has(id)) {
        allFilteredMarkers.push(allMarkers[id]);
      }
    }
    
    
    // Get current map bounds
    const bounds = map ? map.getBounds() : null;
    
    // Filter markers by current bounds
    let visibleMarkers = bounds ? filterMarkersByBounds(allFilteredMarkers, bounds) : allFilteredMarkers;
    
    // For the @googlemaps/markerclusterer library, we need to set the map property
    // on the markers we want to show
    visibleMarkers.forEach(marker => marker.setMap(map));

    // Update the MarkerClusterer with the visible markers.
    // Guard: skip if the OverlayView projection isn't ready — MarkerClusterer's
    // GridAlgorithm uses the overlay's fromLatLngToDivPixel(), NOT the map's
    // projection. These are separate objects with different init timing.
    if (markerCluster && markerCluster.getProjection()) {
      try {

        // Check if we need to clear and re-add markers.
        // WHY clearMarkers(true) (= don't draw yet): a draw with the empty list hides every lone
        // marker the clusterer drew itself and stores the new zoom; at zoom >= maxZoom the
        // GridAlgorithm then says "nothing changed", so the draw in addMarkers() is skipped and
        // those markers stay hidden. One draw with the new list avoids that.
        if (typeof markerCluster.clearMarkers === 'function') {
          markerCluster.clearMarkers(true);
          markerCluster.addMarkers(visibleMarkers);
        } else if (typeof markerCluster.render === 'function') {
          // Some versions of MarkerClusterer use render() to refresh
          markerCluster.render();
        }

      } catch (error) {
        Logger.error('Error updating MarkerClusterer', error);
      }
    }
    
    
    // Return the count of all markers matching the filter criteria (not just visible ones)
    return allFilteredMarkers.length;
  }

  /**
   * Zooms the main map once to the geo bounds given in the URL (?bounds=sw_lat,sw_lng,ne_lat,ne_lng).
   *
   * CONTEXT: ?bounds= is a deep link from the marketing site (area pages such as Jan Thiel send their
   *          area's box). FilterModule only uses it as a list-view filter (DataManager.selectItems skips
   *          it in map view), so without this the map opened on the whole island.
   * UPSTREAM: Called once by createMarkersFromData(), right after FilterModule.init() has read the URL
   *           into filterState.usergeoboundsforlist (validated by UrlState.getBounds()).
   * DOWNSTREAM: map.fitBounds() triggers the map's 'idle' handler, which shows the markers in that area.
   * FAILURE: No-op when there are no (valid) bounds, or when the page opens in list view: the map div
   *          is display:none there, and fitting a hidden map gives a wrong zoom level.
   * @private
   */
  function fitMapToUrlBounds() {
    if (typeof FilterModule === 'undefined' || App.getCurrentView() !== App.VIEW_STATE.MAP) {
      return;
    }
    const urlBounds = FilterModule.getFilterState().usergeoboundsforlist;
    if (map && urlBounds) {
      map.fitBounds(urlBounds);
    }
  }

  // Public interface of the MapModule
  return {
    /**
     * Initializes the Google Map, creates the info window, adds custom controls,
     * and subscribes to the DataManager's 'dataloaded' event to trigger marker creation.
     * This function is intended to be called by the Google Maps API callback (`initMap` global function).
     * @public
     */
    initMap: function() {
      
      // Get the map container element
      const mapContainer = document.getElementById('map');
      if (!mapContainer) {
        Logger.error('Failed to find map container element with ID "map"');
        return;
      }
      
      map = new google.maps.Map(mapContainer, AppConfig.map);
      
      // Add custom control buttons to the map
      this.addCustomControls();
      
      infoWindow = new google.maps.InfoWindow({
        //disableAutoPan: true
      });

      // Clear property from URL when InfoWindow is closed
      google.maps.event.addListener(infoWindow, 'closeclick', () => {
        clearPropertyFromUrl();
      });

      // Focus the View Details button when InfoWindow content is ready
      google.maps.event.addListener(infoWindow, 'domready', () => {
        const viewDetailsBtn = document.querySelector('.info-window .view-details-btn');
        if (viewDetailsBtn) {
          viewDetailsBtn.focus();
        }
      });

      // Set up share button event listeners (uses event delegation, only needs to run once)
      setupShareEventListeners();

      // Add click listener to close info window when clicking outside of it
      google.maps.event.addListener(map, 'click', () => {
        if (infoWindow) {
          infoWindow.close();
          clearPropertyFromUrl();
        }
      });
      
      // Add touch event listener for touch screens
      mapContainer.addEventListener('touchstart', (event) => {
        // Only process if info window is open
        if (infoWindow && infoWindow.getMap()) {
          // Get the info window DOM element
          const infoWindowEl = document.querySelector('.gm-style-iw-a');
          if (infoWindowEl) {
            // Check if the touch is outside the info window
            if (!infoWindowEl.contains(event.target)) {
              infoWindow.close();
              clearPropertyFromUrl();
            }
          }
        }
      }, { passive: true });
      
      
      // Subscribe to data loaded event
      DataManager.subscribe('dataloaded', () => {
        
        // Create markers from data. This also runs FilterModule.init(), whose own 'dataloaded'
        // subscriber applies the URL filters right away (data is loaded, so it fires synchronously).
        // WHY no applyFilters() here any more: a second pass with the same filter state redid
        // setMap() on every marker and two clusterer renders, costing main-thread time on load (TBT).
        this.createMarkersFromData();

        // Hide the global loading indicator
        const loadingIndicator = document.getElementById('loading-indicator');
        if (loadingIndicator) {
          loadingIndicator.style.display = 'none';
        } else {
          Logger.warn('Loading indicator element not found');
        }
        
        // Show the filter panel now that data is available
        if (typeof App !== 'undefined') {
          App.showFilterPanel();
        } else {
          Logger.warn('App module not available to show filter panel');
        }

        // Open property popup if ?property= is in URL (deep-linking)
        const urlProperty = UrlState.getString('property', '', 50);
        if (urlProperty) {
          // Small delay to ensure markers are created and map is ready
          setTimeout(() => {
            updatingPropertyFromUrl = true;
            openPropertyPopupByUrlId(urlProperty);
            updatingPropertyFromUrl = false;
          }, 500);
        }
      });

      // Subscribe to URL changes for browser back/forward navigation (property param)
      UrlState.onChange(function(state) {
        // Only handle if we're in map view and data is loaded
        if (!DataManager.isDataLoaded() || App.getCurrentView() !== App.VIEW_STATE.MAP) {
          return;
        }

        const urlProperty = state.property || '';
        const currentOpenProperty = infoWindow && infoWindow.getMap() ? true : false;

        if (urlProperty && !currentOpenProperty) {
          // URL has property but InfoWindow is closed - open it
          updatingPropertyFromUrl = true;
          openPropertyPopupByUrlId(urlProperty);
          updatingPropertyFromUrl = false;
        } else if (!urlProperty && currentOpenProperty) {
          // URL has no property but InfoWindow is open - close it
          updatingPropertyFromUrl = true;
          infoWindow.close();
          updatingPropertyFromUrl = false;
        }
      });
    },

    /**
     * Creates Google Map markers for all properties available in the DataManager.
     * Initializes the MarkerClusterer and sets up click listeners for each marker
     * to show the info window. Called internally after data is loaded.
     * @public
     */
    createMarkersFromData: function() {
      
      // Get property data from DataManager
      const markersData = DataManager.getAllPropertyData();
      
      // Tracking variables for creation status
      let validMarkersCount = 0;
      let invalidMarkersCount = 0;
      
      // Create markers from the data
      
      // Create markers without adding them to the map yet (we'll add them to the cluster)
      markersData.forEach((markerData, index) => {
        const lat = parseFloat(markerData.lat);
        const lng = parseFloat(markerData.lng);
        const id = markerData.id;
        
        if (isNaN(lat) || isNaN(lng) || !id) {
          invalidMarkersCount++;
          Logger.warn(`Invalid marker data at index ${index}, id: ${id}, lat: ${markerData.lat}, lng: ${markerData.lng}`);
          return;
        }
        
        const marker = new google.maps.Marker({
          position: { lat, lng },
          // Hover title: the listing's title, else its property type. Set here (the data is
          // already loaded) instead of a second setTitle() pass over all markers afterwards.
          title: markerData.property_title || AppUtils.capitalize(markerData.property_type) || 'Property',
          // Don't add to map directly, will be added via MarkerClusterer
          map: null,
          // Custom green dot icon
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor:'#f7A100',//'#269D41', //'#1c7430',
            fillOpacity: 1,
            strokeColor: '#ffffff',//'#165c28',
            strokeWeight: 2,
            scale: 10 // Size of the dot
          }
        });
        
       

        // Store property ID with the marker - this is the only reference to the data
        // we keep with the marker, all other data is fetched from DataManager when needed
        marker.propertyId = id;
        
        // Store marker in collections indexed by property ID
        allMarkers[id] = marker;
        markersArray.push(marker);
        validMarkersCount++;
        
        // Log progress in batches to avoid excessive console output
        if (validMarkersCount % 100 === 0 || validMarkersCount === markersData.length) {
        }
      });

      // Initialize FilterModule BEFORE creating the MarkerClusterer, so that URL
      // params (like ?realtor=) are read and applied to allFilteredMarkers first.
      // Since data is already loaded, the dataloaded callback fires synchronously,
      // which reads URL state and calls showMarkersByIds() to set allFilteredMarkers.
      // Note: markerCluster doesn't exist yet, but showMarkersByIds() handles that
      // gracefully (the if-guard skips the clusterer update when it's null).
      if (typeof FilterModule !== 'undefined') {
        FilterModule.init(map);
      } else {
        Logger.error('Filter module not available');
      }

      // Zoom the map to ?bounds= (e.g. an area page's "open map" button) now that
      // FilterModule has read and validated it from the URL.
      fitMapToUrlBounds();

      // Initialize MarkerClusterer with the filtered markers from allFilteredMarkers
      // (set by FilterModule.init → applyFilters → showMarkersByIds above).
      // We must wait for the map's OverlayView projection to be available, because
      // MarkerClusterer's GridAlgorithm needs fromLatLngToDivPixel() which depends on it.

      /**
       * Sets up the MarkerClusterer once we're sure the map projection is ready.
       * Called either immediately (if projection exists) or after projection_changed fires.
       */
      const initMarkerClusterer = () => {
        try {
          // Check if MarkerClusterer is available
          if (typeof markerClusterer !== 'undefined') {

            // Create a renderer for custom cluster markers
            const renderer = {
              render: ({ count, position }) => {
                // Create custom cluster marker
                const scale = AppConfig.markerCluster.marker.getScale(count);

                return new google.maps.Marker({
                  position,
                  icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    fillColor: AppConfig.markerCluster.marker.colors.base,
                    fillOpacity: 0.9,
                    strokeColor: AppConfig.markerCluster.marker.colors.stroke,
                    strokeWeight: AppConfig.markerCluster.marker.strokeWeight,
                    scale: scale,
                  },
                  label: {
                    text: String(count),
                    color: AppConfig.markerCluster.marker.colors.text,
                    fontSize: AppConfig.markerCluster.marker.fontSize,
                  },
                  zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                });
              }
            };

            // Create the algorithm
            const algorithm = new markerClusterer.GridAlgorithm(AppConfig.markerCluster.algorithm);

            // Get bounds-visible subset of the already-filtered markers
            const bounds = map.getBounds();
            const markersForCluster = bounds
              ? filterMarkersByBounds(allFilteredMarkers, bounds)
              : allFilteredMarkers;

            // Prepare options for MarkerClusterer
            const markerClusterOptions = {
              map,
              markers: markersForCluster,
              algorithm,
              renderer
            };

            // Handle onClusterClick parameter - it should be a function, not a boolean
            if (typeof AppConfig.markerCluster.onClusterClick === 'function') {
              // If it's already a function, use it directly
              markerClusterOptions.onClusterClick = AppConfig.markerCluster.onClusterClick;
            } else if (AppConfig.markerCluster.onClusterClick === true) {
              // If it's true, use the default behavior (zoom in)
              // We don't set onClusterClick in this case, as the default behavior is what we want
            }

            // Initialize MarkerClusterer with options
            markerCluster = new markerClusterer.MarkerClusterer(markerClusterOptions);

            // Add the visible markers to the map
            markersForCluster.forEach(marker => marker.setMap(map));

          } else {
            Logger.error('MarkerClusterer library not available. Make sure it is loaded before initializing the map.');
            // Fallback: add filtered markers directly to the map
            allFilteredMarkers.forEach(marker => marker.setMap(map));
          }
        } catch (error) {
          Logger.error('Error initializing MarkerClusterer', error);
          // Fallback: add filtered markers directly to the map
          allFilteredMarkers.forEach(marker => marker.setMap(map));
        }
      };

      // Wait for the map projection to be ready before initializing MarkerClusterer
      if (map.getProjection()) {
        // Projection already available — safe to init immediately
        initMarkerClusterer();
      } else {
        // Projection not ready yet — wait for the event
        google.maps.event.addListenerOnce(map, 'projection_changed', initMarkerClusterer);
      }

      // Add click listeners directly to markers
      markersArray.forEach(marker => {
        google.maps.event.addListener(marker, 'click', () => {

          // Track clicked marker and update its color if it's the first time
          if (!clickedMarkers.has(marker.propertyId)) {
            clickedMarkers.add(marker.propertyId);
            updateMarkerToClickedState(marker);
          }

          // Create content based on whether data is loaded
          const content = createInfoWindowContent(marker);
          infoWindow.setContent(content);
          infoWindow.open(map, marker);

          // Update URL with property urlid (for sharing/deep-linking)
          writePropertyToUrl(marker.propertyId);

          // If the data isn't loaded yet, set up a check to update the content when it is
          if (!DataManager.isDataLoaded()) {
            let checkCount = 0;
            const infoWindowUpdateInterval = setInterval(() => {
              checkCount++;

              if (DataManager.isDataLoaded() && infoWindow.getMap()) { // Only update if info window is still open
                infoWindow.setContent(createInfoWindowContent(marker));
                clearInterval(infoWindowUpdateInterval);
              } else if (!infoWindow.getMap()) {
                // Clean up interval if info window was closed
                clearInterval(infoWindowUpdateInterval);
              } else if (checkCount > 60) { // 30 seconds max (60 * 500ms)
                Logger.warn('Giving up on waiting for property data after 30 seconds');
                clearInterval(infoWindowUpdateInterval);
              }
            }, 500); // Check every 500ms
          }
        });
      });
      
      // Add bounds changed listener to update visible markers when map is panned or zoomed
      google.maps.event.addListener(map, 'idle', function() {
        // Only update if we have filtered markers and the map is initialized
        if (allFilteredMarkers.length > 0 && map) {
          const bounds = map.getBounds();
          if (bounds) {

            // Filter markers by current bounds
            const visibleMarkers = filterMarkersByBounds(allFilteredMarkers, bounds);

            // Update marker visibility
            allFilteredMarkers.forEach(marker => marker.setMap(null));
            visibleMarkers.forEach(marker => marker.setMap(map));

            // Update the MarkerClusterer with the visible markers.
            // Guard: skip if the OverlayView projection isn't ready — MarkerClusterer's
            // GridAlgorithm uses the overlay's fromLatLngToDivPixel(), NOT the map's
            // projection. These are separate objects with different init timing.
            if (markerCluster && markerCluster.getProjection()) {
              try {
                // WHY clearMarkers(true): see showMarkersByIds(). Without it, zooming in to >= maxZoom
                // (e.g. a ?property= deep link zooms to 16) hid a lone marker for good, and Google Maps
                // closes an InfoWindow whose marker leaves the map: the deep-link popup vanished.
                if (typeof markerCluster.clearMarkers === 'function') {
                  markerCluster.clearMarkers(true);
                  markerCluster.addMarkers(visibleMarkers);
                } else if (typeof markerCluster.render === 'function') {
                  markerCluster.render();
                }
              } catch (error) {
                Logger.error('Error updating MarkerClusterer on bounds change', error);
              }
            }

          }
        }
      });
      
    },

    /**
     * Public interface to show/hide markers based on filter results.
     * Calls the internal `showMarkersByIds` function.
     * @param {Array<string|number>} propertyIds - An array of property IDs to display.
     * @returns {number} The count of markers that were made visible.
     * @public
     */
    showMarkersByIds: function(propertyIds) {
      return showMarkersByIds(propertyIds);
    },

    /**
     * Returns an array containing all created google.maps.Marker objects.
     * @returns {Array<google.maps.Marker>} Array of all markers.
     * @public
     */
    getAllMarkers: function() {
      return markersArray;
    },

    /**
     * Adds custom UI controls (List View button, Filter toggle button) to the
     * top-right corner of the map.
     * @public
     */
    addCustomControls: function() {

      // Create the container div for the buttons
      const controlDiv = document.createElement('div');
      controlDiv.className = 'map-custom-controls';
      
      // Style the container to match Google Maps controls
      controlDiv.style.margin = '10px';
      controlDiv.style.backgroundColor = 'transparent';
      
      // Create a container for the buttons to group them together
      const buttonContainer = document.createElement('div');
      buttonContainer.style.display = 'flex';
      buttonContainer.style.borderRadius = '2px';
      buttonContainer.style.overflow = 'hidden'; // Ensures the border radius applies to children
      
      // Create the List button
      const listButton = document.createElement('button');
      listButton.className = 'map-control-button';
      listButton.innerHTML = `<i class="bi bi-list-ul"></i> ${t('nav.list')}`;
      listButton.title = t('nav.list');


      // Add click handler for List button
      listButton.addEventListener('click', function() {
        App.switchView(App.VIEW_STATE.LIST);
      });

      // Create the Filter button
      const filterButton = document.createElement('button');
      filterButton.className = 'map-control-button';
      filterButton.innerHTML = `<i class="bi bi-funnel"></i> ${t('nav.filter')}`;
      filterButton.title = t('nav.filter');
      
      // Add click handler for Filter button
      filterButton.addEventListener('click', function() {
        App.toggleSidebar();
      });
      
      // Add buttons to the button container
      buttonContainer.appendChild(filterButton);
      buttonContainer.appendChild(listButton);
            
      // Add button container to the main control div
      controlDiv.appendChild(buttonContainer);
      
      // Add the control to the map
      map.controls[google.maps.ControlPosition.TOP_RIGHT].push(controlDiv);
      
    },

    /**
     * Returns the bounds of the google maps viewport as a google.maps.LatLngBounds object.
     * @public
     */
    getBounds: function(){
      return map.getBounds();
    },

    /**
     * Opens the property popup/InfoWindow for a property specified by URL ID.
     * Useful for deep-linking to a specific property.
     *
     * CONTEXT: Public API for opening a property popup programmatically.
     * UPSTREAM: Can be called by other modules or URL state handlers.
     * DOWNSTREAM: Calls internal openPropertyPopupByUrlId().
     *
     * @param {string|number} urlid - The URL ID of the property to show.
     * @public
     */
    openPropertyPopup: function(urlid) {
      openPropertyPopupByUrlId(urlid);
    }
  };
})();

/**
 * Global function called by the Google Maps API script when it finishes loading.
 * Initiates the MapModule's map initialization process.
 *
 * CONTEXT: The Google Maps API callback (see the `&callback=initMap` script tag
 *          in index.html). It is the single entry point into the map after the
 *          external Maps script loads.
 * UPSTREAM: Invoked by the async-loaded Google Maps JS once it has initialized.
 * DOWNSTREAM: Calls MapModule.initMap(), which does `new google.maps.Map(...)`.
 * FAILURE: On flaky connections or with aggressive content blockers the callback
 *          can fire without a usable `google` namespace. Rather than letting
 *          MapModule.initMap() throw an uncaught "google is not defined" that
 *          freezes the loading spinner (Raygun 3o8rizwl), we guard here and show
 *          the same localized reload prompt used for our own missing modules
 *          (showDependencyError, defined inline in index.html, before this
 *          async callback). `typeof google` is ReferenceError-safe, so it is
 *          safe to test a global that may never have been declared.
 *          Same for i18n.js: MapModule.initMap() builds the map buttons with t(),
 *          so without it we stop here (app.js already showed the reload overlay)
 *          instead of throwing "t is not defined" (Raygun 3fz96czh).
 * @global
 */
function initMap() {
  if (typeof google === 'undefined' || !google.maps || typeof google.maps.Map !== 'function') {
    if (typeof showDependencyError === 'function') {
      showDependencyError(['Google Maps']);
    } else {
      console.error('[Map] Google Maps API not available; cannot initialize the map.');
    }
    return;
  }
  if (typeof t !== 'function') {
    return;
  }
  MapModule.initMap();
}
